import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { SectionLabel, formatDate, timeAgo } from './shared';
import useIsMobile from '../../hooks/useIsMobile';

const TIER_LABELS = { free: 'Starter', pro: 'Pro', band: 'Band' };
const TIER_COLORS = { free: '#6c757d', pro: '#667eea', band: '#764ba2' };

function TierBadge({ tier }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      fontSize: 11, fontWeight: 600,
      background: `${TIER_COLORS[tier] || '#6c757d'}22`,
      color: TIER_COLORS[tier] || '#6c757d',
      border: `1px solid ${TIER_COLORS[tier] || '#6c757d'}44`,
      whiteSpace: 'nowrap',
    }}>
      {TIER_LABELS[tier] || tier}
    </span>
  );
}

function TierSelect({ artistId, tier, onSave }) {
  return (
    <select autoFocus defaultValue={tier} onBlur={e => onSave(artistId, e.target.value)} onChange={e => onSave(artistId, e.target.value)}
      style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid #667eea', background: '#1a1a3e', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
      <option value="free">Starter</option>
      <option value="pro">Pro</option>
      <option value="band">Band</option>
    </select>
  );
}

function TenantCard({ artist, editingTier, setEditingTier, updateTier, theme }) {
  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{artist.band_name || artist.artist_name || '—'}</div>
          {artist.band_slug && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>/gig/{artist.band_slug}</div>}
        </div>
        {editingTier === artist.id
          ? <TierSelect artistId={artist.id} tier={artist.subscription_tier || 'free'} onSave={updateTier} />
          : <span onClick={() => setEditingTier(artist.id)} style={{ cursor: 'pointer', flexShrink: 0 }} title="Tap to change tier"><TierBadge tier={artist.subscription_tier || 'free'} /></span>}
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{artist.email || '—'}</div>
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: theme.textMuted }}>
        <span>{artist.song_count} songs</span>
        <span>{artist.gig_count} gigs</span>
        <span>{artist.member_count} login{artist.member_count === 1 ? '' : 's'}</span>
        <span>{formatDate(artist.created_at)}</span>
      </div>
    </div>
  );
}

function TenantTable({ artists, editingTier, setEditingTier, updateTier, theme }) {
  const cell  = { padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, fontSize: 13, color: theme.text, verticalAlign: 'middle' };
  const hCell = { ...cell, fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', background: theme.surfaceAlt, borderBottom: `2px solid ${theme.border}` };

  return (
    <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${theme.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
        <thead>
          <tr>
            <th style={hCell}>Tenant</th>
            <th style={hCell}>Email</th>
            <th style={hCell}>Tier</th>
            <th style={{ ...hCell, textAlign: 'center' }}>Songs</th>
            <th style={{ ...hCell, textAlign: 'center' }}>Gigs</th>
            <th style={{ ...hCell, textAlign: 'center' }}>Logins</th>
            <th style={hCell}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {artists.map(artist => (
            <tr key={artist.id} onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={cell}>
                <div style={{ fontWeight: 600 }}>{artist.band_name || artist.artist_name || '—'}</div>
                {artist.band_slug && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>/gig/{artist.band_slug}</div>}
              </td>
              <td style={{ ...cell, color: theme.textMuted }}>{artist.email || '—'}</td>
              <td style={cell}>
                {editingTier === artist.id
                  ? <TierSelect artistId={artist.id} tier={artist.subscription_tier || 'free'} onSave={updateTier} />
                  : <span onClick={() => setEditingTier(artist.id)} style={{ cursor: 'pointer' }} title="Click to change tier"><TierBadge tier={artist.subscription_tier || 'free'} /></span>}
              </td>
              <td style={{ ...cell, textAlign: 'center', color: theme.textMuted }}>{artist.song_count}</td>
              <td style={{ ...cell, textAlign: 'center', color: theme.textMuted }}>{artist.gig_count}</td>
              <td style={{ ...cell, textAlign: 'center', color: theme.textMuted }}>{artist.member_count}</td>
              <td style={{ ...cell, color: theme.textMuted }}>{formatDate(artist.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One line per audit entry, e.g. "Tier: Starter → Pro"
function describeChange(entry) {
  const b = entry.before || {}, a = entry.after || {};
  if (entry.action === 'tier.change') return `Tier: ${TIER_LABELS[b.tier] || b.tier} → ${TIER_LABELS[a.tier] || a.tier}`;
  if (entry.action === 'role.change') return `Role: ${b.role} → ${a.role}`;
  return entry.action;
}

function AuditLog({ entries, theme }) {
  return (
    <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface }}>
      {entries.map((e, i) => (
        <div key={e.id} style={{ padding: '10px 14px', borderBottom: i < entries.length - 1 ? `1px solid ${theme.border}` : 'none', fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontWeight: 600, color: theme.text }}>{describeChange(e)}</span>
            <span style={{ color: theme.textMuted, whiteSpace: 'nowrap' }} title={new Date(e.at).toLocaleString('en-GB')}>{timeAgo(e.at)}</span>
          </div>
          <div style={{ color: theme.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {e.target} · by {e.actor}
          </div>
        </div>
      ))}
    </div>
  );
}

const OUTCOME_LABELS = {
  success:         { label: 'Signed in',        color: '#22c55e' },
  wrong_password:  { label: 'Wrong password',   color: '#f59e0b' },
  no_account:      { label: 'No such account',  color: '#f59e0b' },
  unverified:      { label: 'Email not confirmed', color: '#f59e0b' },
  rate_limited:    { label: 'Too many attempts', color: '#ef4444' },
  forbidden:       { label: 'Refused',          color: '#ef4444' },
  invalid_request: { label: 'Invalid request',  color: '#ef4444' },
  error:           { label: 'Error',            color: '#ef4444' },
};

// Sign-in attempts (kept 30 days), for support: wrong password, or no account at all?
function SignIns({ theme }) {
  const [entries, setEntries] = useState([]);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get('/api/admin/sign-ins', { params: search.trim() ? { email: search.trim() } : {} })
        .then(r => setEntries(r.data))
        .catch(() => toast.error('Failed to load sign-ins'));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div style={{ marginBottom: 32 }}>
      <SectionLabel theme={theme}>Recent sign-ins</SectionLabel>
      <input
        type="search"
        placeholder="Search by email"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', maxWidth: 320, padding: '8px 10px', marginBottom: 10, borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.surface, color: theme.text, fontSize: 13 }}
      />
      {entries.length === 0
        ? <div style={{ fontSize: 13, color: theme.textMuted }}>No sign-in attempts{search.trim() ? ' for that search' : ''} in the last 30 days.</div>
        : (
          <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface, maxHeight: 360, overflowY: 'auto' }}>
            {entries.map((e, i) => {
              const o = OUTCOME_LABELS[e.outcome] || { label: e.outcome, color: theme.textMuted };
              return (
                <div key={i} style={{ padding: '8px 14px', borderBottom: i < entries.length - 1 ? `1px solid ${theme.border}` : 'none', fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.email}</span>
                    <span style={{ color: theme.textMuted, whiteSpace: 'nowrap' }} title={new Date(e.at).toLocaleString('en-GB')}>{timeAgo(e.at)}</span>
                  </div>
                  <div style={{ marginTop: 2, display: 'flex', gap: 8, color: theme.textMuted, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    <span style={{ color: o.color, fontWeight: 600 }}>{o.label}</span>
                    {e.ip_hash && <span title="Same code = same network address">from {e.ip_hash.slice(0, 6)}</span>}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.user_agent}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export default function TenantsTab({ theme }) {
  const isMobile = useIsMobile(900);
  const [artists, setArtists]         = useState([]);
  const [perArtist, setPerArtist]     = useState([]);
  const [activeGigs, setActiveGigs]   = useState(null);
  const [loadingArtists, setLoading]  = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [auditLog, setAuditLog]       = useState([]);

  const loadAuditLog = useCallback(() => {
    axios.get('/api/admin/audit-log')
      .then(r => setAuditLog(r.data))
      .catch(() => toast.error('Failed to load admin activity'));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get('/api/admin/artists'),
      axios.get('/api/admin/infrastructure'),
    ])
      .then(([a, i]) => {
        setArtists(a.data);
        setPerArtist(i.data.perArtist ?? []);
        setActiveGigs(i.data.activeGigs);
      })
      .catch(() => toast.error('Failed to load tenants'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); loadAuditLog(); }, [load, loadAuditLog]);

  const updateTier = async (artistId, tier) => {
    setEditingTier(null);
    const prev = artists.find(a => a.id === artistId)?.subscription_tier;
    setArtists(a => a.map(x => x.id === artistId ? { ...x, subscription_tier: tier } : x));
    try {
      await axios.patch(`/api/admin/artists/${artistId}/tier`, { tier });
      toast.success('Tier updated');
      loadAuditLog();
    } catch {
      toast.error('Failed to update tier');
      setArtists(a => a.map(x => x.id === artistId ? { ...x, subscription_tier: prev } : x));
    }
  };

  const cell  = { padding: '10px 14px', borderBottom: `1px solid ${theme.border}`, fontSize: 12, color: theme.text, verticalAlign: 'middle' };
  const hCell = { ...cell, fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', background: theme.surfaceAlt, borderBottom: `2px solid ${theme.border}` };

  return (
    <>
      {/* Active gig count */}
      {activeGigs != null && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: theme.textMuted }}>
            <span style={{ fontWeight: 600, color: activeGigs > 0 ? '#22c55e' : theme.text }}>{activeGigs}</span> gig{activeGigs !== 1 ? 's' : ''} currently live
          </span>
        </div>
      )}

      {/* Tenant list */}
      <div style={{ marginBottom: 32 }}>
        <SectionLabel theme={theme}>
          Tenants {!loadingArtists && `— ${artists.length}`}
        </SectionLabel>
        {loadingArtists ? (
          <div style={{ fontSize: 13, color: theme.textMuted }}>Loading…</div>
        ) : artists.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.textMuted }}>No tenants found.</div>
        ) : isMobile ? (
          artists.map(artist => (
            <TenantCard key={artist.id} artist={artist} editingTier={editingTier} setEditingTier={setEditingTier} updateTier={updateTier} theme={theme} />
          ))
        ) : (
          <TenantTable artists={artists} editingTier={editingTier} setEditingTier={setEditingTier} updateTier={updateTier} theme={theme} />
        )}
      </div>

      <SignIns theme={theme} />

      {/* Admin activity (tier and role changes) */}
      <div style={{ marginBottom: 32 }}>
        <SectionLabel theme={theme}>Admin activity</SectionLabel>
        {auditLog.length === 0
          ? <div style={{ fontSize: 13, color: theme.textMuted }}>No admin changes recorded yet.</div>
          : <AuditLog entries={auditLog} theme={theme} />}
      </div>

      {/* Per-tenant footprint */}
      {perArtist.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <SectionLabel theme={theme}>Per-tenant footprint</SectionLabel>
          <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: theme.surface }}>
              <thead>
                <tr>
                  <th style={hCell}>Tenant</th>
                  <th style={{ ...hCell, textAlign: 'center' }}>Songs</th>
                  <th style={{ ...hCell, textAlign: 'center' }}>Gigs</th>
                  <th style={{ ...hCell, textAlign: 'center' }}>Requests</th>
                </tr>
              </thead>
              <tbody>
                {perArtist.map((a, i) => (
                  <tr key={a.id} onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...cell, fontWeight: 600, borderBottom: i < perArtist.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{a.name}</td>
                    <td style={{ ...cell, textAlign: 'center', color: theme.textMuted, borderBottom: i < perArtist.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{a.songs}</td>
                    <td style={{ ...cell, textAlign: 'center', color: theme.textMuted, borderBottom: i < perArtist.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{a.gigs}</td>
                    <td style={{ ...cell, textAlign: 'center', color: theme.textMuted, borderBottom: i < perArtist.length - 1 ? `1px solid ${theme.border}` : 'none' }}>{a.requests}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
